// enclosingScopes — what each query level around a subquery body lets that
// body reference, innermost level first. The rules were probed on the real
// servers (PostgreSQL 16, MySQL 8.0, SQL Server 2025, Oracle 23, SQLite 3):
//
//  - an expression subquery (WHERE / SELECT list / SET …) sees all of its
//    level's sources, and the levels further out;
//  - a subquery in a JOIN's ON sees what that ON sees (`onVisibleItems`, the
//    ON editor's rule): its own join tree up to that JOIN — every earlier item
//    on SQLite, whose comma is an ordinary join. In an UPDATE / DELETE the
//    rule runs over the relation list the ON is in, as the text reads it
//    (`dmlRelationListing`): the structure keeps the first item of MySQL's /
//    SQL Server's `DELETE o FROM orders o JOIN c ON …` as the DELETE target,
//    and none of MySQL's `UPDATE a, b JOIN c ON … SET`;
//  - in a compound (UNION …) level only the subquery's own branch counts;
//  - a LATERAL / CROSS|OUTER APPLY body sees the FROM items before it in
//    source order, comma items and JOINs mixed, never one after it (nor an
//    UPDATE's target: PostgreSQL rejects it). SQL Server's APPLY sees only its
//    own comma-separated table source (`FROM a, b CROSS APPLY (… a.x …)` is
//    Msg 4104); PostgreSQL, MySQL and Oracle see every earlier item;
//  - a plain derived table in FROM — a MERGE's USING subquery, a derived
//    table in a MySQL `UPDATE a JOIN (…) x` — sees none of its siblings, and
//    the levels further out stay visible (every engine accepts that outer
//    reference; MySQL since 8.0.14);
//  - a CTE body sees none of its WITH statement's own FROM items, and the
//    levels outside that statement stay visible (PostgreSQL, MySQL, SQLite) —
//    except on Oracle, which rejects any outer reference from a CTE body
//    (ORA-00904), so the walk stops there. SQL Server allows WITH only at the
//    start of a statement: its CTE bodies have no level further out.
//
// Only what a level lets the body see hides the same names further out: a
// sibling after a LATERAL, a comma item outside an APPLY's table source or
// the ON's join tree, a derived table's siblings and a WITH statement's own
// FROM items are invisible there, and an outer alias spelled like one stays
// reachable (every engine binds it).
//
// A level's own text lacks the WITH clauses around it, so an unqualified name
// that an enclosing WITH declares as a CTE is re-read as that CTE (the
// extractor's own rule) — only the WITHs a level sits in (their main query,
// or a CTE body: its earlier siblings and itself), never a WITH nested inside
// it. Cached per parsed buffer (one document version).

import type { ParseOptions, SqlRange } from '../../sql-grammar/model';
import type { CteBinding, ParsedBuffer } from '../types';
import type { FromItem, JoinKind, QueryStructure } from '../types/queryStructure';
import { dmlRelationListing, type ListedItem } from './dmlRelationLists';
import { enclosingQueryLevels, type EnclosingQueryLevel } from './enclosingQueryLevels';
import { onVisibleIndices, onVisibleItems } from './joinOnVisibility';

export type EnclosingScope = {
  /** The level's query — its own compound branch — extracted over `text` (offsets are into `text`). */
  readonly query: QueryStructure;
  /** The level's own text, the subquery below cut down to `(SELECT 1)`. */
  readonly text: string;
  /**
   * What the subquery below sees here — every source of `query`, or just
   * these FROM items — and so what hides the same names further out.
   */
  readonly sees: 'all' | readonly FromItem[];
  /**
   * An UPDATE / DELETE level whose JOIN ON the subquery is in: the start
   * offsets of the relation-list items it sees. The tables of those lists the
   * structure does not carry (`listedDmlSources`) count only when listed here.
   */
  readonly listed?: ReadonlySet<number>;
};

const LATERAL_KINDS: ReadonlySet<JoinKind> = new Set(['lateral', 'cross-apply', 'outer-apply']);
const cache = new WeakMap<ParsedBuffer, Map<string, readonly EnclosingScope[]>>();

/** Innermost level first, up to (not past) a barrier. */
export function enclosingScopes(
  buffer: ParsedBuffer,
  statementId: string,
  bodyStart: number,
  bodyEnd: number,
): readonly EnclosingScope[] {
  let perBuffer = cache.get(buffer);
  if (!perBuffer) {
    perBuffer = new Map();
    cache.set(buffer, perBuffer);
  }
  const key = `${statementId}:${bodyStart}-${bodyEnd}`;
  const cached = perBuffer.get(key);
  if (cached) return cached;
  const levels = enclosingQueryLevels(buffer, statementId, bodyStart, bodyEnd);
  const options = buffer.parsedDocument.options;
  const inherited = inheritedCtes(levels);
  const scopes: EnclosingScope[] = [];
  for (let k = levels.length - 1; k >= 0; k--) {
    const scope = scopeOf(levels[k]!, options);
    if (scope === 'stop') break;
    if (scope) {
      const ctes = inherited[k]!;
      const { query, sees } = scope;
      scopes.push({ ...scope, query: withCteRefs(query, ctes), sees: sees === 'all' ? sees : sees.map((i) => cteRef(i, ctes)) });
    }
  }
  perBuffer.set(key, scopes);
  return scopes;
}

/**
 * Per level, the CTEs the WITHs around it declare and it may name, innermost
 * WITH first: a WITH reaches its main query and everything inside it, and a
 * CTE body (and everything inside it) sees that WITH's earlier CTEs and
 * itself — never a later sibling (`resolveScope`'s cte-def rule).
 */
function inheritedCtes(levels: readonly EnclosingQueryLevel[]): (readonly CteBinding[])[] {
  const out: (readonly CteBinding[])[] = [];
  let reach: readonly CteBinding[] = [];
  for (const level of levels) {
    out.push(reach);
    const ctes = level.structure?.ctes ?? [];
    const body = ctes.find((c) => within(c.bodyRange, level.frameOpen));
    const passed = body ? ctes.filter((c) => c === body || c.bodyRange.endOffset <= body.bodyRange.startOffset) : ctes;
    reach = [...passed, ...reach];
  }
  return out;
}

type Item = { readonly item: FromItem; readonly kind?: JoinKind; readonly comma: boolean; readonly target: boolean };

/** One level's scope; null when it adds nothing (the walk goes on); 'stop' at a barrier. */
function scopeOf(level: EnclosingQueryLevel, options: ParseOptions): EnclosingScope | null | 'stop' {
  const { structure, frameOpen: at, text } = level;
  if (!structure) return 'stop';
  if ((structure.ctes ?? []).some((c) => within(c.bodyRange, at))) return options.engine === 'oracle' ? 'stop' : null;
  const query = branchAt(structure, at);
  if (!query) return null;
  const items = itemsInOrder(query);
  const holder = items.find(({ item }) => within(item.range, at));
  if (!holder) {
    const dml = query.kind === 'update' || query.kind === 'delete' ? query.kind : null;
    if (dml !== null) {
      const listing = dmlRelationListing(text, 0, text.length, dml, options);
      // A derived table of an UPDATE / DELETE relation list the structure does not carry.
      if (listing.derived.some(([open]) => open === at)) return { query, text, sees: [] };
      const listed = onVisibleListed(listing.lists, at, options);
      if (listed) {
        const sees = items.map(({ item }) => item).filter((item) => listed.has(item.range.startOffset));
        return { query, text, sees, listed };
      }
    }
    const on = (query.joins ?? []).findIndex((j) => within(j.range, at));
    if (on >= 0) return { query, text, sees: onVisibleItems(query, on, options.engine) };
    return { query, text, sees: 'all' };
  }
  const lateral = level.lateral || (holder.kind !== undefined && LATERAL_KINDS.has(holder.kind));
  if (holder.target || !lateral) return { query, text, sees: [] };
  const chainStart =
    options.engine === 'mssql'
      ? Math.max(-1, ...items.filter((i) => i.comma && i.item.range.startOffset < at).map((i) => i.item.range.startOffset))
      : -1;
  const before = items.filter(
    ({ item, target }) => !target && item.range.endOffset <= at && item.range.startOffset >= chainStart,
  );
  return { query, text, sees: before.map(({ item }) => item) };
}

/** Start offsets of the relation-list items a subquery in an ON at `at` sees; null when `at` is in no ON. */
function onVisibleListed(lists: readonly (readonly ListedItem[])[], at: number, options: ParseOptions): Set<number> | null {
  for (const list of lists) {
    const k = list.findIndex(({ on }) => on !== undefined && on[0] <= at && at < on[1]);
    if (k < 0) continue;
    const visible = onVisibleIndices(
      list.map(({ joined }) => joined),
      k,
      options.engine,
    );
    return new Set(visible.map((i) => list[i]!.start));
  }
  return null;
}

/** The compound branch holding `at` (the level itself when it is not compound); null past every branch. */
function branchAt(structure: QueryStructure, at: number): QueryStructure | null {
  const branches = structure.compoundBranches ?? [];
  if (branches.length === 0) return structure;
  const later = branches.find((b) => within(b.statementRange, at));
  if (later) return later;
  const firstOperator = structure.compoundOperators?.[0]?.range.startOffset;
  return firstOperator !== undefined && at < firstOperator ? structure : null;
}

/** The level's FROM items in source order: DML targets, comma items and joined items. */
function itemsInOrder(s: QueryStructure): Item[] {
  const targets = [s.updateTarget, s.deleteTarget, s.mergeTarget, s.mergeSource].filter(
    (item): item is FromItem => item != null,
  );
  return [
    ...targets.map((item) => ({ item, comma: false, target: true })),
    ...(s.fromItems ?? []).map((item) => ({ item, comma: true, target: false })),
    ...(s.joins ?? []).map((j) => ({ item: j.right, kind: j.kind, comma: false, target: false })),
  ].sort((a, b) => a.item.range.startOffset - b.item.range.startOffset);
}

/** `item` read as the CTE it names when it is an unqualified relation named like one of `ctes`. */
function cteRef(item: FromItem, ctes: readonly CteBinding[]): FromItem {
  const t = item.target;
  if (t.kind !== 'relation' || t.qualified.foldedSchema) return item;
  const cte = ctes.find((c) => c.name.foldedName === t.qualified.foldedName);
  return cte ? { ...item, target: { kind: 'cte-ref', cte } } : item;
}

function withCteRefs(s: QueryStructure, ctes: readonly CteBinding[]): QueryStructure {
  if (ctes.length === 0) return s;
  return {
    ...s,
    ...(s.updateTarget ? { updateTarget: cteRef(s.updateTarget, ctes) } : {}),
    ...(s.deleteTarget ? { deleteTarget: cteRef(s.deleteTarget, ctes) } : {}),
    ...(s.fromItems ? { fromItems: s.fromItems.map((item) => cteRef(item, ctes)) } : {}),
    ...(s.joins ? { joins: s.joins.map((j) => ({ ...j, right: cteRef(j.right, ctes) })) } : {}),
  };
}

const within = (range: SqlRange, at: number): boolean => range.startOffset <= at && at < range.endOffset;
